import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";
import Reveal from "@/website/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Star, GoogleLogo, ArrowUpRight, Info, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Reviews() {
  usePageMeta({ title: "Reviews", path: "/reviews", description: "Real reviews from real customers across Albury, Wodonga and surrounds." });
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ customer_name: "", rating: 5, text: "", service: "", email: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/public/google-reviews")
      .then((r) => setData(r.data))
      .catch((err) => console.warn("[reviews] google-reviews load failed:", err?.message || err));
  }, []);

  const reviews = data?.reviews || [];
  const avg = data?.rating ? Number(data.rating).toFixed(1) : (reviews.length ? (reviews.reduce((s,r)=>s+r.rating,0)/reviews.length).toFixed(1) : "—");
  const total = data?.total ?? reviews.length;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.text.trim()) return toast.error("Name and review are required");
    setSubmitting(true);
    try {
      await api.post("/public/reviews", { ...form, rating: Number(form.rating) });
      setSubmitted(true);
      toast.success("Thanks! Your review is pending approval.");
    } catch { toast.error("Could not submit your review"); } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="reviews-page">
      {/* Hero — matches Gallery / About / Locations dark navy theme */}
      <section data-nav-theme="dark" className="bg-navy-deep text-white py-20 min-h-[60vh] flex items-center px-5 md:px-10 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none"/>
        <div className="relative max-w-[1400px] mx-auto">
          <Reveal>
            <div className="eyebrow">Reviews · social proof</div>
            <h1 className="font-display display-lg tracking-wide mt-3 leading-tight">
              Real words.<br/>Real customers.
            </h1>
            <p className="mt-4 text-slate-300 max-w-2xl text-sm md:text-base">
              Every review here comes from a real job we've completed across Albury, Wodonga &amp; the region.
            </p>
          </Reveal>

          <Reveal delay={150} className="mt-10 md:mt-12 grid md:grid-cols-3 gap-8 md:gap-10 items-end pb-10 border-b border-white/15">
            <div>
              <div className="eyebrow !text-white/50 mb-2">Average rating</div>
              <div className="flex items-end gap-3">
                <span className="font-display text-6xl md:text-7xl tracking-wide text-white">{avg}</span>
                <div className="pb-2">
                  <div className="flex gap-0.5">{Array.from({length:5}).map((_,i)=><Star key={`star-${i}`} size={14} weight="fill" className={i < Math.round(parseFloat(avg)||0) ? "text-white":"text-white/25"}/>)}</div>
                  <div className="text-xs text-white/60 mt-1">{total} review{total===1?"":"s"}</div>
                </div>
              </div>
            </div>
            <div>
              <div className="eyebrow !text-white/50 mb-2">Source</div>
              <div className="flex items-center gap-2 text-white font-medium">
                {data?.source === "google" ? (<><GoogleLogo size={20} weight="fill"/>Google Business Profile</>) : (<>Direct reviews from customers</>)}
              </div>
            </div>
            <div className="flex md:justify-end gap-2 flex-wrap">
              {data?.write_review_url && (
                <a href={data.write_review_url} target="_blank" rel="noreferrer" className="pill-light"><GoogleLogo size={14} weight="fill"/>Write a review</a>
              )}
              {data?.google_url && (
                <a href={data.google_url} target="_blank" rel="noreferrer" className="pill-ghost"><ArrowUpRight size={14} weight="bold"/>View on Google</a>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Reviews grid — cream section like Gallery */}
      <section data-nav-theme="light" className="bg-cream px-5 md:px-10 py-20">
        <div className="max-w-[1400px] mx-auto">
          {reviews.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Info size={28} className="mx-auto mb-3 text-slate-300"/>
              <p className="text-sm">No reviews yet. Be our first — leave one below.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {reviews.map((r, i) => (
                <Reveal key={`${r.time || ""}-${r.author_name || i}`} delay={i * 70}>
                  <div className="bg-paper border-t-2 border-navy p-7 h-full">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3">
                        {r.profile_photo_url && <img src={r.profile_photo_url} alt="" className="w-10 h-10 rounded-full object-cover"/>}
                        <div>
                          <div className="font-medium text-navy">{r.author_name}</div>
                          <div className="flex gap-0.5 mt-0.5">{Array.from({length:5}).map((_,j)=><Star key={`s-${j}`} size={11} weight={j < r.rating ? "fill" : "regular"} className={j < r.rating ? "text-navy" : "text-slate-300"}/>)}</div>
                        </div>
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400">{r.relative_time || (r.time ? new Date(r.time).toLocaleDateString() : "")}</div>
                    </div>
                    <p className="text-slate-700 font-light text-base leading-relaxed">"{r.text}"</p>
                    {r.author_url && (
                      <a href={r.author_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-navy"><GoogleLogo size={12} weight="fill"/>View on Google <ArrowUpRight size={10}/></a>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Leave a review form */}
      <section data-nav-theme="light" className="bg-paper px-5 md:px-10 py-20 border-t border-slate-200">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="eyebrow eyebrow-dark text-center">Leave a review</div>
            <h2 className="font-display display-lg tracking-wide text-navy mt-3 leading-[0.95] text-center">Worked with us?<br/>Let people know.</h2>
            <p className="mt-3 text-center text-slate-600 font-light max-w-xl mx-auto">Your review helps your neighbours find a tradie they can trust. Reviews are reviewed before being published.</p>
          </Reveal>
          <Reveal delay={150}>
            {submitted ? (
              <div className="mt-12 text-center bg-cream p-10 rounded-sm border border-slate-200">
                <CheckCircle size={42} weight="fill" className="text-navy mx-auto"/>
                <h3 className="font-display text-2xl tracking-wide text-navy mt-4">Thanks for the kind words.</h3>
                <p className="text-slate-600 mt-2 font-light">We'll review and publish your review shortly.</p>
                {data?.write_review_url && (
                  <a href={data.write_review_url} target="_blank" rel="noreferrer" className="pill-navy mt-6 inline-flex"><GoogleLogo size={14} weight="fill"/>Also leave it on Google</a>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="mt-12 space-y-4 bg-cream p-6 md:p-8 rounded-sm border border-slate-200">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-500">Your name</label>
                    <Input data-testid="rev-name" required value={form.customer_name} onChange={(e)=>setForm({...form, customer_name: e.target.value})} className="rounded-sm mt-1 h-11"/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-500">Service we did</label>
                    <Input data-testid="rev-service" value={form.service} onChange={(e)=>setForm({...form, service: e.target.value})} placeholder="e.g. gutter cleaning" className="rounded-sm mt-1 h-11"/>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-500">Your rating</label>
                  <div className="mt-2 flex gap-1">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} type="button" data-testid={`rev-star-${n}`} onClick={()=>setForm({...form, rating: n})} className="p-1 transition-transform hover:scale-110">
                        <Star size={28} weight={n <= form.rating ? "fill" : "regular"} className={n <= form.rating ? "text-navy" : "text-slate-300"}/>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-500">Your review</label>
                  <Textarea data-testid="rev-text" rows={4} required value={form.text} onChange={(e)=>setForm({...form, text: e.target.value})} placeholder="What did we do for you? How was the experience?" className="rounded-sm mt-1"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-500">Email (optional, never published)</label>
                  <Input data-testid="rev-email" type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="rounded-sm mt-1 h-11"/>
                </div>
                <Button data-testid="rev-submit" type="submit" disabled={submitting} className="pill-navy !py-3.5 w-full justify-center !text-sm">{submitting ? "Submitting…" : "Submit review"}</Button>
                {data?.write_review_url && (
                  <p className="text-xs text-slate-500 text-center">Prefer Google? <a href={data.write_review_url} target="_blank" rel="noreferrer" className="text-navy underline">Leave a Google review</a> instead.</p>
                )}
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
